import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './chatBubble.css';
import messageChatIcon from 'assets/icons/leal_role_chat.png';
import { ReactComponent as SendMessageIcon } from 'assets/icons/send_message_2.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close_2.svg';
import { ChatLoader } from '../ChatLoader/ChatLoader';
import { AssistantMessages } from '../AssistantMessages/AssistantMessages';
import { CHAT_ROLES } from '../constants';

export const ChatBubble = ({
  isOpen,
  onClose,
  chatHistory = [],
  onSendMessage = () => {
  },
  isLoading = false,
  avatarSrc = messageChatIcon,
  inputPlaceholder = 'Ask anything ',
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 0);
    }
  }, [chatHistory, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleSendClick = () => {
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendClick();
    }
  };

  return (
    <div className="chat-bubble" role="dialog" aria-modal="false" aria-labelledby="chat-bubble-title">
      <div className="chat-bubble-header">
        <div id="chat-bubble-title" className="chat-bubble-title">
          AI oncology expert
        </div>
        <div className="chat-bubble-header-buttons">
          <button
            type="button"
            className="chat-bubble-header-button"
            onClick={onClose}
            aria-label="Close chat"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <div className="chat-bubble-content">
        <div className="chat-bubble-message-area">
          {chatHistory.map((msg, index) => (
            <div key={index} className={`chat-bubble-message ${msg.role === CHAT_ROLES.ME ? 'user' : 'assistant'}`}>
              {msg.role !== CHAT_ROLES.ME && avatarSrc && (
                <img src={avatarSrc} alt="Assistant avatar" className="chat-bubble-avatar" />
              )}
              <div className="chat-bubble-message-text-container">
                {Array.isArray(msg.content) ? (
                  <AssistantMessages messages={msg.content} />
                ) : (
                  <p className="chat-bubble-message-text">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="chat-bubble-loader-container">
              <ChatLoader />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-bubble-input-area">
        <input
          type="text"
          className="chat-bubble-input"
          placeholder={inputPlaceholder}
          aria-label={inputPlaceholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          type="button"
          className="chat-bubble-send-button"
          onClick={handleSendClick}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
        >
          <SendMessageIcon />
        </button>
      </div>
    </div>
  );
};

ChatBubble.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chatHistory: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string.isRequired,
      content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string.isRequired,
            content: PropTypes.any,
          }),
        ),
      ]).isRequired,
    }),
  ),
  onSendMessage: PropTypes.func,
  isLoading: PropTypes.bool,
  avatarSrc: PropTypes.string,
  inputPlaceholder: PropTypes.string,
};
